import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Form, Input, InputNumber, Badge, Select } from 'antd';
import { Button, Dialog, Message } from '@alifd/next';
import { Box } from '@mui/material';
import { Rank, SettingFormValues } from './types';
import { FormProps } from 'antd/es/form';
import client from '@/lib/feathers';

const { useForm, Item, List } = Form;

const QuestionnaireSettings: React.FC<{
  setIsUpdate: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setIsUpdate }) => {
  const [visible, setVisible] = useState(false);
  const [initialValues, setInitialValues] = useState<SettingFormValues>({
    ranks: [
      {
        rank: '預設等級一',
        start: 0,
        end: 100,
      },
    ],
    date: {
      questionnaireAgendaDay: '1',
      questionnaireExpireDay: '28',
    },
  });
  const [hasError, setHasError] = useState<{
    [tab: string]: boolean;
  }>({ ranks: false, dates: false });

  const [settingForm] = useForm<SettingFormValues>();

  useEffect(() => {
    const getSetting = async () => {
      try {
        const res = await client.service('communityData').find({
          query: {
            $select: [
              'questionnaireRank',
              'questionnaireAgendaDay',
              'questionnaireExpireDay',
            ],
          },
        });
        if (res.data.length > 0) {
          const {
            questionnaireRank,
            questionnaireAgendaDay,
            questionnaireExpireDay,
          } = res.data[0];
          if (
            questionnaireRank &&
            questionnaireAgendaDay &&
            questionnaireExpireDay
          ) {
            setInitialValues({
              ranks: questionnaireRank.map((rank: Rank) => ({
                rank: rank.rank,
                start: rank.range[0],
                end: rank.range[1],
              })),
              date: {
                questionnaireAgendaDay: questionnaireAgendaDay,
                questionnaireExpireDay: questionnaireExpireDay,
              },
            });
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    if (visible) {
      getSetting();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      settingForm.setFieldsValue(initialValues);
    }
  }, [initialValues, settingForm, visible]);

  const createRangeValidator = useCallback(
    (type: 'start' | 'end', index: number) => {
      return {
        validator(_: any, value: number) {
          let isRangeConflict = false;
          if (typeof value === 'number') {
            settingForm.getFieldsValue().ranks.forEach((rank, i) => {
              if (index !== i) {
                if (
                  typeof rank.start === 'number' &&
                  typeof rank.end === 'number'
                ) {
                  if (value > rank.start && value < rank.end) {
                    isRangeConflict = true;
                  }
                  if (value === rank[type]) {
                    isRangeConflict = true;
                  }
                }
              }
            });
          }
          return isRangeConflict
            ? Promise.reject('範圍重疊')
            : Promise.resolve();
        },
      };
    },
    [settingForm]
  );

  const handleFinish: FormProps['onFinish'] = async (
    values: SettingFormValues
  ) => {
    const newRanks: Rank[] = values.ranks.map((rank) => ({
      rank: rank.rank,
      range: [rank.start, rank.end],
    }));

    try {
      await client.service('communityData').patch(null, {
        questionnaireRank: newRanks.sort((a, b) => b.range[0] - a.range[0]),
        questionnaireAgendaDay: values.date.questionnaireAgendaDay,
        questionnaireExpireDay: values.date.questionnaireExpireDay,
      });
      Message.success('儲存成功');
      setIsUpdate(true);
      setVisible(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    settingForm.resetFields();
  };

  return (
    <>
      <Button onClick={() => setVisible(true)}>問卷相關設定</Button>
      <Dialog
        title="問卷相關設定"
        visible={visible}
        onClose={handleCancel}
        onCancel={handleCancel}
        onOk={() => settingForm.submit()}
      >
        <Form
          form={settingForm}
          initialValues={initialValues}
          onFinish={handleFinish}
          onFinishFailed={() => Message.error('請檢查錯誤欄位')}
          onFieldsChange={(changeFields, allFields) => {
            const errorFields = allFields.filter(
              (field) => field.errors && field.errors.length > 0
            );
            const errorNames = [
              ...new Set(errorFields.map((field) => field.name[0])),
            ];
            setHasError({
              ranks: errorNames.includes('ranks'),
              dates: errorNames.includes('dates'),
            });
          }}
          onValuesChange={(changeFields) => {
            if (changeFields.ranks) {
              settingForm.validateFields();
            }
          }}
        >
          <Tabs defaultActiveKey="ranks">
            <Tabs.TabPane
              tab={<Badge dot={hasError['ranks']}>等級設定</Badge>}
              key="ranks"
              forceRender
            >
              <Box
                sx={{
                  width: '900px',
                  height: '400px',
                  overflowY: 'auto',
                }}
              >
                <List
                  name="ranks"
                  rules={[
                    {
                      validator: (_, ranks: SettingFormValues['ranks']) => {
                        const sortRanks = [...ranks];
                        sortRanks.sort((a, b) => b.start - a.start);
                        let errorMessage: string | undefined;
                        sortRanks.forEach((rank, i, arr) => {
                          const nextRank = arr[i + 1];
                          if (rank) {
                            if (i === 0) {
                              if (rank.end !== 100) {
                                errorMessage = '結束值必須為100';
                              }
                            }
                            if (nextRank) {
                              if (rank.start !== nextRank.end) {
                                errorMessage = '範圍必須連續';
                              }
                            } else {
                              if (rank.start !== 0) {
                                errorMessage = '起始值必須為0';
                              }
                            }
                          }
                        });
                        return errorMessage
                          ? Promise.reject(new Error(errorMessage))
                          : Promise.resolve();
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) => {
                    return (
                      <>
                        {fields.map((field, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '300px 200px 200px 100px',
                              width: '100%',
                              gap: '25px',
                            }}
                          >
                            <Item
                              name={[index, 'rank']}
                              label="等級"
                              rules={[
                                { required: true, message: '請輸入等級名稱' },
                              ]}
                            >
                              <Input placeholder="請輸入等級名稱" />
                            </Item>
                            <Item
                              name={[index, 'start']}
                              rules={[
                                { required: true, message: '請輸入等級下限' },
                                createRangeValidator('start', index),
                              ]}
                            >
                              <InputNumber
                                min={0}
                                max={99}
                                placeholder="請輸入等級下限"
                                style={{ width: '100%' }}
                              />
                            </Item>
                            <Item
                              name={[index, 'end']}
                              rules={[
                                {
                                  required: true,
                                  message: '請輸入等級上限',
                                },
                                createRangeValidator('end', index),
                              ]}
                            >
                              <InputNumber
                                min={1}
                                max={100}
                                placeholder="請輸入等級上限"
                                style={{ width: '100%' }}
                              />
                            </Item>
                            {index !== 0 && (
                              <Button
                                warning
                                onClick={() => {
                                  remove(field.name);
                                }}
                              >
                                刪除
                              </Button>
                            )}
                          </Box>
                        ))}
                        <Item>
                          <Button onClick={() => add()}>新增等級</Button>
                        </Item>
                        <Form.ErrorList errors={errors} />
                      </>
                    );
                  }}
                </List>
              </Box>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <Badge text="error" color="red">
                  日期設定
                </Badge>
              }
              key="dates"
              forceRender
            >
              <Box
                sx={{
                  width: '900px',
                  height: '400px',
                  overflowY: 'auto',
                }}
              >
                <Item
                  name={['date', 'questionnaireAgendaDay']}
                  label="每月問卷自動產生日"
                  rules={[
                    {
                      required: true,
                      message: '請輸入每月問卷自動產生日',
                    },
                  ]}
                >
                  <Select
                    options={Array(15)
                      .fill(0)
                      .map((_, i) => ({
                        label: `${i + 1}號`,
                        value: `${i + 1}`,
                      }))}
                    allowClear={false}
                    style={{ width: '100px' }}
                  />
                </Item>
                <Item
                  name={['date', 'questionnaireExpireDay']}
                  label="每月問卷截止日"
                  rules={[
                    {
                      required: true,
                      message: '請輸入每月問卷截止日',
                    },
                  ]}
                >
                  <Select
                    options={Array(13)
                      .fill(0)
                      .map((_, i) => ({
                        label: `${i + 16}號`,
                        value: `${i + 16}`,
                      }))}
                    allowClear={false}
                    style={{ width: '100px' }}
                  />
                </Item>
              </Box>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Dialog>
    </>
  );
};

export default QuestionnaireSettings;
